import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Broadcast, CheckCircle, WarningCircle, ArrowsClockwise, Buildings, Globe, EnvelopeSimple, PaperPlaneTilt, Clock, Tag, Broom } from "@phosphor-icons/react";
import SharedTermsPanel from "./SharedTermsPanel";
import TargetingPanel from "./TargetingPanel";
import SourceCoveragePanel from "./SourceCoveragePanel";
import JobProvidersPanel from "./JobProvidersPanel";
import HasDataCollection from "./HasDataCollection";
import BackfillCampaign from "./BackfillCampaign";import GoogleJobsCollection from "./GoogleJobsCollection";
import ApifyCollection from "./ApifyCollection";
import BrightDataJobsCollection from "./BrightDataJobsCollection";

const DEPUIS = [{ v: "1", l: "24 h" }, { v: "3", l: "3 jours" }, { v: "7", l: "7 jours" }, { v: "14", l: "14 jours" }, { v: "31", l: "31 jours" }];
const HOURS = Array.from({ length: 24 }, (_, h) => ({ v: String(h), l: `${String(h).padStart(2, "0")}:00 UTC` }));

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminSources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({});
  const [syncing, setSyncing] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [alertsCfg, setAlertsCfg] = useState(null);
  const [alertsBusy, setAlertsBusy] = useState(false);
  const [domBusy, setDomBusy] = useState(false);
  const [domResult, setDomResult] = useState(null);
  const [exaCfg, setExaCfg] = useState(null);
  const [exaBusy, setExaBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, dg, al, ex] = await Promise.all([
        api.get("/admin/signal-sources"),
        api.get("/admin/digest/config").catch(() => ({ data: null })),
        api.get("/admin/alerts/config").catch(() => ({ data: null })),
        api.get("/admin/signal-sources/exa/config").catch(() => ({ data: null })),
      ]);
      setSources(data);
      if (dg.data) setDigest(dg.data);
      if (al.data) setAlertsCfg(al.data);
      if (ex.data) setExaCfg(ex.data);
      setCfg((c) => {
        const next = { ...c };
        data.forEach((s) => { if (!next[s.key]) next[s.key] = { publiee_depuis: "7", max_offers: "500", adzuna_max_requests: "60", exa_max_queries: "15" }; });
        return next;
      });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveExa = async (patch) => {
    setExaBusy(true);
    try {
      const { data } = await api.post("/admin/signal-sources/exa/config", patch);
      setExaCfg(data);
      toast.success("Budget Exa mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setExaBusy(false); }
  };

  const saveDigest = async (patch) => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/admin/digest/config", patch);
      setDigest(data);
      toast.success("Récapitulatif quotidien mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setDigestBusy(false); }
  };

  const runDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/admin/digest/run");
      if (data.new_signals === 0) toast.info("Aucune nouvelle opportunité à envoyer aujourd'hui.");
      else toast.success(`${data.sent} email(s) envoyé(s) · ${data.new_signals} nouvelle(s) opportunité(s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setDigestBusy(false); }
  };

  const saveAlerts = async (patch) => {
    setAlertsBusy(true);
    try {
      const { data } = await api.post("/admin/alerts/config", patch);
      setAlertsCfg(data);
      toast.success("Alertes personnalisées mises à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setAlertsBusy(false); }
  };

  const runAlerts = async () => {
    setAlertsBusy(true);
    try {
      const { data } = await api.post("/admin/alerts/run");
      if (!data.emails) toast.info(`Aucune nouvelle correspondance à envoyer (${data.searches} recherche(s) évaluée(s)).`);
      else toast.success(`${data.emails} email(s) envoyé(s) · ${data.new_matches} nouvelle(s) correspondance(s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setAlertsBusy(false); }
  };

  const normalizeDomains = async () => {
    setDomBusy(true);
    try {
      const { data } = await api.post("/admin/companies/normalize-domains");
      setDomResult(data);
      toast.success(`${data.updated} domaine(s) nettoyé(s) sur ${data.scanned} entreprise(s)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setDomBusy(false); }
  };

  const setField = (key, field) => (e) => {    const val = e?.target ? e.target.value : e;
    setCfg((c) => ({ ...c, [key]: { ...c[key], [field]: val } }));
  };

  const sync = async (key) => {
    setSyncing(key);
    try {
      const body = {
        publiee_depuis: Number(cfg[key].publiee_depuis),
        max_offers: Number(cfg[key].max_offers),
        adzuna_max_requests: Number(cfg[key].adzuna_max_requests) || 60,
        exa_max_queries: Number(cfg[key].exa_max_queries) || 15,
      };
      const { data } = await api.post(`/admin/signal-sources/${key}/sync`, body);
      if (data.error) { toast.error(data.error); setSyncing(null); return; }
      toast.success("Synchronisation lancée en arrière-plan — le résultat s'affichera dans quelques instants.");
      // Poll la source jusqu'à la fin du run (évite le timeout proxy : le travail tourne côté serveur)
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const { data: srcs } = await api.get("/admin/signal-sources");
          setSources(srcs);
          const s = (srcs || []).find((x) => x.key === key);
          const done = s?.last_run && s.last_run.status !== "running";
          if (done || tries >= 40) {
            clearInterval(poll);
            setSyncing(null);
            if (done) {
              const lr = s.last_run;
              if (lr.error) toast.error(lr.error);
              else toast.success(`${lr.fetched} offre(s) · ${lr.created} créé(s), ${lr.updated} maj · ${lr.intermediaries} intermédiaire(s)`);
            }
          }
        } catch { /* keep polling */ }
      }, 8000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setSyncing(null);
    }
  };

  return (
    <div data-testid="admin-sources">
      <div className="overline mb-2">Acquisition de signaux</div>
      <h1 className="font-display text-3xl font-bold mb-2">Sources de signaux</h1>
      <p className="text-muted text-sm mb-6 max-w-2xl">Connectez des sources externes d'offres d'emploi. Les offres sont normalisées, dédoublonnées et taguées « client final » ou « ESN ». Les cabinets de recrutement et agences d'intérim sont automatiquement exclus. La source et les liens d'origine restent invisibles pour les ESN (admin uniquement).</p>

      {/* Maintenance données — nettoyage des domaines (retire www./https://) */}
      <div className="surface rounded-lg p-5 mb-6 flex items-start justify-between gap-4 flex-wrap" data-testid="domain-cleanup-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center shrink-0">
            <Broom size={20} weight="duotone" className="text-accent" />
          </div>
          <div>
            <h3 className="font-display font-bold text-fg">Nettoyer les domaines</h3>
            <p className="text-xs text-muted max-w-xl mt-0.5">Normalise les domaines déjà stockés (retire <span className="font-mono">www.</span>, <span className="font-mono">https://</span>, chemins) pour fiabiliser la découverte de décideurs et l'enrichissement Prospeo. Action idempotente — relançable sans risque.</p>
            {domResult && (
              <div className="mt-2 text-xs" data-testid="domain-cleanup-result">
                <span className="text-accent font-semibold">{domResult.updated}</span> nettoyé(s) sur {domResult.scanned} entreprise(s).
                {(domResult.examples || []).slice(0, 3).map((ex, i) => (
                  <div key={i} className="font-mono text-[11px] text-muted mt-0.5"><span className="line-through">{ex.before}</span> → <span className="text-fg/80">{ex.after}</span></div>
                ))}
              </div>
            )}
          </div>
        </div>
        <button onClick={normalizeDomains} disabled={domBusy} data-testid="normalize-domains-btn"
          className="flex items-center gap-2 border border-accent/40 text-accent font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9 whitespace-nowrap">
          <Broom size={15} weight="fill" className={domBusy ? "animate-pulse" : ""} /> {domBusy ? "Nettoyage…" : "Nettoyer les domaines"}
        </button>
      </div>

      {/* Phase C — Nouveaux Job Providers (JSearch / TheirStack / Fantastic.jobs) */}
      <JobProvidersPanel />
      {/* B1-B4 — Couverture par source d'origine + priorités + santé/routes */}
      <SourceCoveragePanel />

      {/* Shared keywords + locations — single source for ALL providers */}
      <TargetingPanel />

      {/* HasData — Indeed France collection (runs) */}
      <HasDataCollection />

      {/* HasData — one-time controlled backfill campaign (FR 75% / US 25%) */}
      <BackfillCampaign />

      {/* SerpApi — Google Jobs aggregator (country config / test mode / cost / runs) */}
      <GoogleJobsCollection />

      {/* Apify — French IT freelance market (Free-Work / Freelance-Informatique / Codeur) */}
      <ApifyCollection />

      {/* Bright Data — job listings collection (dataset auto-discovered, simulate-safe in preview) */}
      <BrightDataJobsCollection />

      {/* Daily digest — morning recap email to every team member */}
      {digest && (
        <div className="surface rounded-lg p-6 mb-6" data-testid="digest-card">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center">
                <EnvelopeSimple size={20} weight="duotone" className="text-accent" />
              </div>
              <div>
                <h3 className="font-display font-bold text-fg">Récapitulatif quotidien</h3>
                <p className="text-xs text-muted">Un email chaque matin à chaque membre avec les nouvelles opportunités IT détectées.</p>
              </div>
            </div>
            <button onClick={() => saveDigest({ enabled: !digest.enabled })} disabled={digestBusy} data-testid="digest-toggle"
              className={`flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-medium ${digest.enabled ? "bg-accent/12 border-accent/40 text-accent" : "bg-surface border-border text-muted hover:text-fg"}`}>
              <CheckCircle size={14} weight={digest.enabled ? "fill" : "regular"} /> {digest.enabled ? "Activé" : "Désactivé"}
            </button>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 border-t border-border pt-4">
            <div>
              <label className="overline block mb-1.5 flex items-center gap-1"><Clock size={12} /> Heure d'envoi</label>
              <Select value={String(digest.hour)} onValueChange={(v) => saveDigest({ hour: Number(v) })}>
                <SelectTrigger className="bg-surface-2 border-border text-xs h-9" data-testid="digest-hour"><SelectValue /></SelectTrigger>
                <SelectContent>{HOURS.map((h) => <SelectItem key={h.v} value={h.v}>{h.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline block mb-1.5">Opportunités par email</label>
              <Select value={String(digest.max_items)} onValueChange={(v) => saveDigest({ max_items: Number(v) })}>
                <SelectTrigger className="bg-surface-2 border-border text-xs h-9" data-testid="digest-max"><SelectValue /></SelectTrigger>
                <SelectContent>{["5", "8", "10", "15", "20"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <button onClick={runDigest} disabled={digestBusy} data-testid="digest-run-now"
                className="w-full flex items-center justify-center gap-2 border border-accent/40 text-accent font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9">
                <PaperPlaneTilt size={15} weight="fill" /> Envoyer maintenant
              </button>
            </div>
          </div>

          <div className="mt-3 text-xs text-muted" data-testid="digest-lastrun">
            Dernier envoi : <span className="font-mono text-fg">{fmtDate(digest.last_run)}</span>
            {digest.last_run && <span className="font-mono"> · {digest.last_sent} email(s) · {digest.last_new_signals} opportunité(s)</span>}
          </div>
        </div>
      )}

      {/* Personalized opportunity alerts — one email per ESN based on their saved searches */}
      {alertsCfg && (
        <div className="surface rounded-lg p-6 mb-6" data-testid="alerts-card">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center">
                <PaperPlaneTilt size={20} weight="duotone" className="text-accent" />
              </div>
              <div>
                <h3 className="font-display font-bold text-fg">Alertes d'opportunités personnalisées</h3>
                <p className="text-xs text-muted max-w-xl">Un email par ESN basé sur ses recherches sauvegardées. Envoyé uniquement s'il existe au moins un nouveau signal correspondant.</p>
              </div>
            </div>
            <button onClick={() => saveAlerts({ enabled: !alertsCfg.enabled })} disabled={alertsBusy} data-testid="alerts-toggle"
              className={`flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-medium ${alertsCfg.enabled ? "bg-accent/12 border-accent/40 text-accent" : "bg-surface border-border text-muted hover:text-fg"}`}>
              <CheckCircle size={14} weight={alertsCfg.enabled ? "fill" : "regular"} /> {alertsCfg.enabled ? "Activé" : "Désactivé"}
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 border-t border-border pt-4">
            <div>
              <label className="overline block mb-1.5 flex items-center gap-1"><Clock size={12} /> Heure d'envoi</label>
              <Select value={String(alertsCfg.hour)} onValueChange={(v) => saveAlerts({ hour: Number(v) })}>
                <SelectTrigger className="bg-surface-2 border-border text-xs h-9" data-testid="alerts-hour"><SelectValue /></SelectTrigger>
                <SelectContent>{HOURS.map((h) => <SelectItem key={h.v} value={h.v}>{h.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <button onClick={runAlerts} disabled={alertsBusy} data-testid="alerts-run-now"
                className="w-full flex items-center justify-center gap-2 border border-accent/40 text-accent font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9">
                <PaperPlaneTilt size={15} weight="fill" /> Envoyer maintenant
              </button>
            </div>
          </div>

          <div className="mt-3 text-xs text-muted" data-testid="alerts-lastrun">
            Dernier envoi : <span className="font-mono text-fg">{fmtDate(alertsCfg.last_run)}</span>
            {alertsCfg.last_run && <span className="font-mono"> · {alertsCfg.last_emails} email(s) · {alertsCfg.last_searches} recherche(s) · {alertsCfg.last_new_matches} correspondance(s)</span>}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {sources.map((s) => {
            const c = cfg[s.key] || {};
            const isSync = syncing === s.key;
            return (
              <div key={s.key} className="surface rounded-lg p-6" data-testid={`source-${s.key}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center">
                      <Broadcast size={20} weight="duotone" className="text-accent" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-fg">{s.name}</h3>
                      <a href={s.docs} target="_blank" rel="noreferrer" className="text-xs text-muted hover:text-accent flex items-center gap-1"><Globe size={11} /> {s.docs.replace("https://", "")}</a>
                    </div>
                  </div>
                  {s.configured ? (
                    <span className="flex items-center gap-1 text-xs font-mono text-accent border border-accent/30 rounded-full px-2 py-1" data-testid={`source-status-${s.key}`}><CheckCircle size={12} weight="fill" /> Configurée</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs font-mono text-amber-400 border border-amber-500/30 rounded-full px-2 py-1" data-testid={`source-status-${s.key}`}><WarningCircle size={12} weight="fill" /> Clé manquante</span>
                  )}
                </div>
                <p className="text-sm text-muted mb-4">{s.description}</p>

                <div className="space-y-3 border-t border-border pt-4">
                  <p className="text-xs text-muted flex items-center gap-1.5"><Tag size={13} className="text-accent" /> Mots-clés &amp; localisations : gérés dans le panneau partagé en haut de page.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {s.key === "france_travail" ? (
                      <div>
                        <label className="overline block mb-1.5">Publiées depuis</label>
                        <Select value={c.publiee_depuis} onValueChange={setField(s.key, "publiee_depuis")}>
                          <SelectTrigger className="bg-surface-2 border-border text-xs h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{DEPUIS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ) : s.key === "exa" ? (
                      <div>
                        <label className="overline block mb-1.5">Requêtes IA / run</label>
                        <Input type="number" className="bg-surface-2 border-border text-fg text-xs h-9" value={c.exa_max_queries || ""} onChange={setField(s.key, "exa_max_queries")} data-testid="exa-max-queries" />
                      </div>
                    ) : (
                      <div>
                        <label className="overline block mb-1.5">Max appels API (pagination)</label>
                        <Input type="number" className="bg-surface-2 border-border text-fg text-xs h-9" value={c.adzuna_max_requests || ""} onChange={setField(s.key, "adzuna_max_requests")} data-testid={`source-cap-${s.key}`} />
                      </div>
                    )}
                    <div>
                      <label className="overline block mb-1.5">Max offres</label>
                      <Input type="number" className="bg-surface-2 border-border text-fg text-xs h-9" value={c.max_offers || ""} onChange={setField(s.key, "max_offers")} />
                    </div>
                  </div>
                  {s.key === "adzuna" && <p className="text-[11px] text-muted">Adzuna est <strong>national</strong> et parcourt <strong>toutes les pages</strong> de chaque mot-clé (par lots, dans la limite des appels API et du max d'offres).</p>}
                  {s.key === "france_travail" && <p className="text-[11px] text-muted">France Travail reste <strong>national</strong> (filtre IT ROME) — les localisations partagées s'appliquent à Indeed / Adzuna / Google Jobs.</p>}
                  {s.key === "exa" && <p className="text-[11px] text-muted">Exa recherche sur le web (missions freelance &amp; besoins IT en France) ce que les job-boards ne couvrent pas. Chaque requête ≈ 0,007 $ — le <strong>budget mensuel</strong> ci-dessous coupe automatiquement au-delà.</p>}
                  {s.key === "exa" && exaCfg && (
                    <div className="border border-accent/25 bg-accent/5 rounded-md p-3 mt-1" data-testid="exa-budget-panel">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="overline block mb-1.5">Budget mensuel ($)</label>
                          <div className="flex gap-2">
                            <Input type="number" className="bg-surface-2 border-border text-fg text-xs h-9" defaultValue={exaCfg.monthly_budget_usd}
                              onBlur={(e) => { const v = Number(e.target.value); if (v >= 0 && v !== exaCfg.monthly_budget_usd) saveExa({ monthly_budget_usd: v }); }}
                              data-testid="exa-budget-input" />
                        </div>
                        </div>
                        <div>
                          <label className="overline block mb-1.5">Consommé ({exaCfg.usage?.month})</label>
                          <div className="text-xs font-mono text-fg h-9 flex items-center" data-testid="exa-usage">
                            {Number(exaCfg.usage?.spent_usd || 0).toFixed(3)} $ / {Number(exaCfg.monthly_budget_usd).toFixed(0)} $
                            <span className="text-muted ml-2">· {exaCfg.usage?.requests || 0} req</span>
                          </div>
                        </div>
                      </div>
                      {exaCfg.usage?.exhausted && <p className="text-[11px] text-amber-400 mt-2" data-testid="exa-budget-warning">Budget mensuel atteint — augmentez le plafond pour relancer la collecte Exa.</p>}
                      {exaBusy && <p className="text-[11px] text-muted mt-1">Enregistrement…</p>}
                    </div>
                  )}
                </div>

                {s.last_run && (
                  <div className="mt-4 text-xs bg-surface-2 border border-border rounded-md px-3 py-2" data-testid={`source-lastrun-${s.key}`}>
                    <div className="text-muted mb-0.5">Dernière synchro : <span className="font-mono text-fg">{fmtDate(s.last_run.at)}</span></div>
                    {s.last_run.status === "running" ? (
                      <div className="text-amber-400 font-mono animate-pulse">⏳ Collecte en cours en arrière-plan…</div>
                    ) : s.last_run.error ? (
                      <div className="text-destructive">{s.last_run.error}</div>
                    ) : (
                      <div className="text-fg/80 font-mono">{s.last_run.fetched} récupérées · {s.last_run.created} créées · {s.last_run.updated} maj · {s.last_run.intermediaries} intermédiaires</div>
                    )}
                  </div>
                )}

                <button onClick={() => sync(s.key)} disabled={isSync || !s.configured} data-testid={`source-sync-${s.key}`}
                  className="mt-4 w-full flex items-center justify-center gap-2 bg-accent text-black font-semibold px-4 py-2.5 rounded-md hover-lift disabled:opacity-50 disabled:cursor-not-allowed">
                  <ArrowsClockwise size={16} weight="bold" className={isSync ? "animate-spin" : ""} /> {isSync ? "Synchronisation…" : "Synchroniser maintenant"}
                </button>
                {!s.configured && <p className="text-xs text-muted mt-2 text-center">Ajoutez les identifiants API dans la configuration serveur pour activer cette source.</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
